/**
 * ADR-0048 phase 2 — the "Origin" section on a catalogue item, in the
 * PRODUCTION planner only.
 *
 * The mirror of promote.ts. Where that one says "this could go to
 * production", this one says "this CAME from training" — and offers the
 * correction Ed chose over an approval queue: take it back out, and let the
 * author promote it again.
 *
 * Renders nothing at all on items production authored itself, which is most
 * of the catalogue. An "Origin: authored here" section on every item would
 * be noise, and phase 2 exists to make the unusual case visible, not to
 * annotate the usual one.
 */
import { escapeHtml } from "../utils/html";
import { firestoreDatabaseId } from "../firebase";
import { state } from "../state";
import { callCatalogueWithdraw } from "../admin/cf-client";
import {
  canWithdraw,
  isPromotedFromTraining,
  provenanceSentence,
  withdrawConfirmText,
  withdrawnNotice,
  type PromotionProvenance,
} from "./provenance-view";

/**
 * Render the section into `container` (the item's detail panel).
 *
 * `onWithdrawn` lets the caller refresh the tree, since a withdrawal changes
 * `active` and the item should leave the list the same way a retire does.
 */
export function renderProvenanceSection(
  container: HTMLElement,
  collection: string,
  docId: string,
  provenance: PromotionProvenance | null | undefined,
  onWithdrawn?: () => void,
): void {
  if (!isPromotedFromTraining(provenance)) return;
  if (!docId) return;

  const sentence = provenanceSentence(provenance);
  const withdrawn = withdrawnNotice(provenance);
  const mayWithdraw = canWithdraw(firestoreDatabaseId, state.authClaims?.role, provenance)
    && !withdrawn;

  const section = document.createElement("div");
  section.className = "detail-section catalogue-provenance";
  section.innerHTML = `
    <h4>Origin</h4>
    <div class="provenance-story">${escapeHtml(sentence ?? "")}</div>
    ${withdrawn ? `<div class="provenance-withdrawn" style="margin-top:6px;color:#92400e;">${escapeHtml(withdrawn)}</div>` : ""}
    ${mayWithdraw ? `
      <div class="provenance-actions" style="margin-top:8px;">
        <button class="btn-outline" id="withdrawFromProduction"
                title="Retire this item here so the training author can correct it and promote it again">
          Withdraw from production
        </button>
      </div>
      <div class="provenance-confirm" id="withdrawConfirm" hidden style="margin-top:8px;"></div>
    ` : ""}
    <div class="provenance-status" id="withdrawStatus" aria-live="polite" style="margin-top:6px;"></div>
  `;
  container.appendChild(section);

  if (!mayWithdraw) return;

  const status = section.querySelector<HTMLElement>("#withdrawStatus")!;
  const confirmBox = section.querySelector<HTMLElement>("#withdrawConfirm")!;
  const button = section.querySelector<HTMLButtonElement>("#withdrawFromProduction")!;

  // Two-step inline confirmation rather than a modal or a native confirm().
  // A native dialog blocks the page, and the existing delete modal's wording
  // ("cannot be undone") is exactly what must NOT be said here — the whole
  // design is that it CAN be undone, by promoting again.
  button.addEventListener("click", () => {
    const title = (provenance?.sourceDocId ?? docId);
    confirmBox.innerHTML = `
      <div style="white-space:pre-wrap;">${escapeHtml(withdrawConfirmText(title, provenance))}</div>
      <div style="margin-top:8px;display:flex;gap:8px;">
        <button class="btn-delete" id="withdrawYes">Withdraw</button>
        <button class="btn-outline" id="withdrawNo">Keep it</button>
      </div>`;
    confirmBox.hidden = false;
    button.hidden = true;

    confirmBox.querySelector("#withdrawNo")!.addEventListener("click", () => {
      confirmBox.hidden = true;
      confirmBox.innerHTML = "";
      button.hidden = false;
    });

    confirmBox.querySelector("#withdrawYes")!.addEventListener("click", () => {
      void (async () => {
        confirmBox.hidden = true;
        status.textContent = "Withdrawing…";
        try {
          const result = await callCatalogueWithdraw({ collection, docId });
          status.textContent = result.alreadyWithdrawn
            ? "Already withdrawn by someone else."
            : "Withdrawn from production. The training author can promote it again.";
          onWithdrawn?.();
        } catch (err) {
          // Verbatim: the server's refusal names the actual problem.
          status.textContent = err instanceof Error ? err.message : "Could not withdraw.";
          confirmBox.hidden = true;
          button.hidden = false;
        }
      })();
    });
  });
}
